export type MockRole = 'buyer' | 'seller' | 'admin';

const EMAIL_KEY = 'mock-login-email';

export function setMockLoginEmail(email?: string | null) {
  if (typeof localStorage === 'undefined') return;
  if (email) localStorage.setItem(EMAIL_KEY, email);
  else localStorage.removeItem(EMAIL_KEY);
}

export function getMockLoginEmail(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(EMAIL_KEY);
}

export function roleFromEmail(email?: string | null): MockRole {
  const prefix = (email ?? '').trim().toLowerCase();
  if (prefix.startsWith('admin')) return 'admin';
  if (prefix.startsWith('seller')) return 'seller';
  return 'buyer';
}

export function getMockIdentity(email?: string | null) {
  const role = roleFromEmail(email ?? getMockLoginEmail());

  const user = {
    id: `mock-${role}-001`,
    email: email ?? `${role}@procurtal.com`,
    phoneNumber: '+966500000000',
    name: role === 'buyer' ? 'Ahmed Al-Rashid' : role === 'seller' ? 'Khalid Al-Mansour' : 'Admin User',
    role: role === 'admin' ? 'admin' : 'user',
    companyName: role === 'buyer' ? 'Gulf Procurement Solutions' : 'TechSource Electronics Trading',
    crNumber: role === 'buyer' ? '1010456789' : '1010987654',
    taxNumber: role === 'buyer' ? '300456789000003' : '300987654000003',
    contactName: role === 'buyer' ? 'Ahmed Al-Rashid' : 'Khalid Al-Mansour',
    street: role === 'buyer' ? 'King Fahd Road, Building 12' : 'Prince Sultan Street, Office 305',
    street2: role === 'buyer' ? 'Al Olaya District' : 'Al Mohammadiyah District',
    city: 'Riyadh',
    zip: role === 'buyer' ? '12211' : '12363',
    state: { id: 1, name: 'Riyadh Province' },
    country: { id: 194, name: 'Saudi Arabia' },
    profileImage: null,
    emailVerified: true,
    phoneVerified: true,
    twoFactorEnabled: false,
    passwordChangedAt: '2025-06-01T00:00:00.000Z',
    createdAt: '2025-01-01T00:00:00.000Z',
    updatedAt: '2025-06-01T00:00:00.000Z',
    documents: [
      { type: 'CR', uploaded: true },
      { type: 'TAX_CARD', uploaded: true },
    ],
    pendingChangeRequests: [],
    bankAccounts: role === 'seller' ? [
      {
        id: 'ba-001',
        bankName: 'Al Rajhi Bank',
        accountHolder: 'TechSource Electronics Trading',
        iban: 'SA03 8000 0000 6080 1016 7519',
        swift: 'RJHISARI',
        isDefault: true,
        createdAt: '2025-01-15T00:00:00.000Z',
        updatedAt: '2025-01-15T00:00:00.000Z',
      },
    ] : [],
    partnerId: role === 'buyer' ? 1 : 2,
    loyaltyPoints: role === 'buyer' ? 1250 : 800,
  };

  const partner = {
    id: role === 'buyer' ? 1 : 2,
    name: user.companyName,
    email: user.email,
    phone: '+966500000000',
    customerRank: role === 'seller' ? 0 : 1,
    supplierRank: role === 'seller' ? 1 : 0,
    companyType: 'company',
    companyRegistry: user.crNumber,
    country: { id: 194, name: 'Saudi Arabia', code: 'SA' },
    state: { id: 1, name: 'Riyadh Province' },
    vat: user.taxNumber,
    street: user.street,
    street2: user.street2,
    city: user.city,
    zip: user.zip,
    active: true,
    pricelist: null,
  };

  return { user, partner, role };
}

// Backward-compatible static exports (default = buyer).
export const mockUser = getMockIdentity('buyer@procurtal.com').user;
export const mockPartner = getMockIdentity('buyer@procurtal.com').partner;

// Pre-configured demo seed accounts with full profiles.
export const DEMO_ACCOUNTS = [
  { email: 'buyer@procurtal.com', password: 'demo', label: 'Buyer', description: 'Gulf Procurement Solutions' },
  { email: 'seller@procurtal.com', password: 'demo', label: 'Seller', description: 'TechSource Electronics Trading' },
] as const;
